import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaShieldAlt,
  FaLock,
  FaCertificate,
  FaServer,
  FaDatabase,
  FaGlobe,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const TrustSection: React.FC = () => {
  const { t, ready } = useTranslation();
  const bgColor = useColorModeValue('gray.50', 'gray.800');

  const securityFeatures = [
    {
      icon: FaShieldAlt,
      title: t('trustSection.bankLevelSecurity.title'),
      description: t('trustSection.bankLevelSecurity.description'),
      color: 'green.500',
    },
    {
      icon: FaLock,
      title: t('trustSection.dataProtection.title'),
      description: t('trustSection.dataProtection.description'),
      color: 'blue.500',
    },
    {
      icon: FaCertificate,
      title: t('trustSection.gamingLicenses.title'),
      description: t('trustSection.gamingLicenses.description'),
      color: 'purple.500',
    },
    {
      icon: FaServer,
      title: t('trustSection.infrastructureSecurity.title'),
      description: t('trustSection.infrastructureSecurity.description'),
      color: 'orange.500',
    },
    {
      icon: FaDatabase,
      title: t('trustSection.backupRecovery.title'),
      description: t('trustSection.backupRecovery.description'),
      color: 'teal.500',
    },
    {
      icon: FaGlobe,
      title: t('trustSection.globalCompliance.title'),
      description: t('trustSection.globalCompliance.description'),
      color: 'red.500',
    },
  ];

  if (!ready) {
    return null;
  }

  return (
    <Box py={{ base: 12, md: 16 }} bg={bgColor}>
      <Container maxW='7xl'>
        <VStack spacing={{ base: 8, md: 12 }}>
          <VStack spacing={4} textAlign='center'>
            <Heading size={{ base: 'xl', md: '2xl' }} color='primary.500'>
              {t('trustSection.title')}
            </Heading>
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color='gray.500'
              maxW='2xl'
            >
              {t('trustSection.subtitle')}
            </Text>
          </VStack>

          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing={{ base: 6, md: 8 }}
          >
            {securityFeatures.map((feature, index) => (
              <VStack
                key={index}
                spacing={{ base: 3, md: 4 }}
                p={{ base: 4, md: 6 }}
                bg={useColorModeValue('white', 'gray.700')}
                borderRadius='lg'
                boxShadow='md'
                border='1px solid'
                borderColor={useColorModeValue('gray.200', 'gray.600')}
                textAlign='center'
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                transition='all 0.3s'
              >
                <Icon
                  as={feature.icon}
                  boxSize={{ base: 8, md: 10 }}
                  color={feature.color}
                />
                <Heading
                  size={{ base: 'sm', md: 'md' }}
                  color={useColorModeValue('gray.900', 'white')}
                >
                  {feature.title}
                </Heading>
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  color={useColorModeValue('gray.600', 'gray.400')}
                >
                  {feature.description}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>

          <Box
            bg='primary.500'
            color='white'
            p={{ base: 6, md: 8 }}
            borderRadius='xl'
            textAlign='center'
            w='full'
            maxW='4xl'
          >
            <VStack spacing={{ base: 3, md: 4 }}>
              <Heading size={{ base: 'md', md: 'lg' }}>
                {t('trustSection.trustedByIndustry.title')}
              </Heading>
              <Text fontSize={{ base: 'md', md: 'lg' }} maxW='2xl'>
                {t('trustSection.trustedByIndustry.description')}
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default TrustSection;
